// Banner displayed at the top of every admin analytics page.
// Calls /api/admin/health/analytics, picks the worst-case status and tells the
// admin in plain French what's going on. Without this banner the dashboards
// silently show stale or zero data and look "broken" instead of "no data yet".

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, XCircle } from "lucide-react";
import {
  analyticsApi,
  type AnalyticsHealthStatus,
  type AnalyticsPipelineHealth,
} from "@/lib/admin-api/analytics";

interface BannerStyle {
  readonly bg: string;
  readonly border: string;
  readonly text: string;
  readonly icon: typeof AlertTriangle;
  readonly title: string;
}

const STYLES: Record<AnalyticsHealthStatus, BannerStyle> = {
  ok: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-800",
    icon: CheckCircle2,
    title: "Données à jour",
  },
  stale: {
    bg: "bg-amber-50",
    border: "border-amber-300",
    text: "text-amber-900",
    icon: Clock,
    title: "Données obsolètes",
  },
  empty: {
    bg: "bg-stone-50",
    border: "border-stone-300",
    text: "text-stone-800",
    icon: AlertTriangle,
    title: "Aucune donnée pour le moment",
  },
  degraded: {
    bg: "bg-red-50",
    border: "border-red-300",
    text: "text-red-900",
    icon: XCircle,
    title: "Pipeline analytique en erreur",
  },
};

function describePipeline(p: AnalyticsPipelineHealth): string {
  if (p.status === "ok") return `${p.name} : OK`;
  if (p.status === "empty") return `${p.name} : aucune donnée`;
  if (p.status === "stale") {
    const lag = p.lag_hours ?? 0;
    if (lag >= 24) return `${p.name} : retard ${Math.round(lag / 24)} j`;
    return `${p.name} : retard ${Math.round(lag)} h`;
  }
  if (p.status === "degraded") {
    const reason = p.watermark_error ?? "erreur inconnue";
    return `${p.name} : ${reason}`;
  }
  return p.name;
}

function bannerCopy(
  status: AnalyticsHealthStatus,
  problemPipelines: readonly AnalyticsPipelineHealth[],
): string {
  if (status === "ok") {
    return "Tous les pipelines analytiques sont à jour.";
  }
  if (status === "empty") {
    return "Le suivi des événements est en cours d'activation. Les premiers chiffres apparaîtront dès que vos clients commenceront à parcourir le site.";
  }
  if (status === "stale") {
    return "Les agrégations n'ont pas été rafraîchies récemment. Vérifiez que les crons Vercel /api/cron/analytics/* sont actifs.";
  }
  // degraded
  const first = problemPipelines[0]?.watermark_error;
  return first
    ? `Une étape du pipeline a échoué : ${first}`
    : "Une étape du pipeline analytique a échoué. Consultez les logs Vercel.";
}

export function AnalyticsFreshnessBanner() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["analytics", "health"],
    queryFn: () => analyticsApi.health(),
    // Refetch every 5 min — staleness only changes slowly.
    refetchInterval: 5 * 60_000,
    // Don't block dashboards if health endpoint is unreachable.
    retry: 1,
  });

  if (isLoading || isError || !data) return null;
  if (data.status === "ok") return null; // hide green banner — no news is good news

  const style = STYLES[data.status];
  const Icon = style.icon;
  const problems = data.pipelines.filter((p) => p.status !== "ok");

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${style.bg} ${style.border} ${style.text}`}
    >
      <Icon size={20} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-semibold">{style.title}</p>
        <p className="text-sm">{bannerCopy(data.status, problems)}</p>
        {problems.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs opacity-80">
            {problems.map((p) => (
              <li key={p.name}>• {describePipeline(p)}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
